<!--
  App bar — CHAT-856 Design v5.

  Three zones, in this order:

    LEFT    product identity — logo, "CloudRadial Chat Admin", tier badge.
    CENTRE  impersonation, and nothing else. Absent unless you are impersonating.
    RIGHT   notifications · announcements · help · you.

  What is deliberately NOT here any more:

    · The tenant <select>. Every tenant on the environment, unsearchable, in the
      navbar of every page, for the ~5 people who can use it. It is a page now:
      /partners (CHAT-856 commit 1).
    · The standing org/partner name. Own-org users already know whose account
      they are in; the name only carries information while impersonating, which
      is exactly where it now appears.
    · The multi-org <select>. Org switching is superadmin impersonation; regular
      users are single-org under 1-account:1-bot (Design v4).
    · The bare logout icon. Sign out lives in the user menu, with Account and
      Appearance.
-->
<nav class="navbar topnavbar" role="navigation">

  <!-- ── LEFT: what app you are in ─────────────────────────────────────────── -->
  <ul class="navbar-nav flex-row align-items-center header-left">
    <li class="nav-item">
      <!-- Logo + two-weight wordmark, in AutomationAI's brand lockup: full-colour
           product mark, "CloudRadial" bold, the product name lighter. -->
      <a class="navbar-brand" href="#/" aria-label="CloudRadial ChatAI — home">
        <div class="brand-logo">
          <div class="brand-icon">
            <img class="img-fluid" src="assets/img/crchat-logo-square.png" alt="" />
          </div>
          <div class="brand-text">
            <strong>CloudRadial</strong>
            <span class="brand-text-thin">ChatAI</span>
          </div>
        </div>
        <div class="brand-logo-collapsed">
          <img class="img-fluid" src="assets/img/crchat-logo-square.png" alt="CloudRadial ChatAI" />
        </div>
      </a>
    </li>
    <!-- The licence tier reads as part of the product name, not as a property of
         some org named elsewhere in the bar — which is why v5 moved it here. -->
    <li class="nav-item" *ngIf="showTierBadge()">
      <span class="tier-badge d-inline-flex align-items-center"
            [ngClass]="getTierBadgeClass()"
            [title]="getTierDisplayName() + ' plan'">
        <i class="d-flex align-items-center me-1" [ngClass]="getTierIcon()"></i>
        <span class="tier-text">{{getTierDisplayName()}}</span>
      </span>
    </li>
  </ul>

  <!-- ── CENTRE: impersonation ─────────────────────────────────────────────────
       Centred and gold because this is the one thing in the bar you must not be
       able to miss. Keyed off the impersonator_id claim alone rather than
       `isSuperAdmin && impersonating`: if the profile were slow or failed to
       load, the safety marker must still appear. The name beside the eye is the
       impersonated account, resolved separately (CHAT-959) and never blank. -->
  <div class="header-impersonation" *ngIf="isImpersonating">
    <span class="impersonation-badge d-inline-flex align-items-center"
          [title]="'You are impersonating ' + currentTenantName">
      <em class="icon-eye me-1"></em>
      <span class="impersonation-name">{{currentTenantName}}</span>
    </span>
    <button type="button"
            kendoButton fillMode="flat" size="small" class="btn-stop-impersonating"
            (click)="stopImpersonation()"
            title="Return to your own organization">
      Stop
    </button>
  </div>

  <!-- ── CENTRE, quiet state: the bot you are in (CHAT-1152) ──────────────────
       Same slot as the impersonation badge, which stays its loud state. Shown
       only to someone who could be in another bot; see showTenantBadge(). -->
  <div class="header-impersonation header-tenant" *ngIf="showTenantBadge()">
    <span class="tenant-badge d-inline-flex align-items-center"
          [title]="'You are in ' + currentBotName">
      <span class="impersonation-name">{{currentBotName}}</span>
    </span>
  </div>

  <!-- ── RIGHT: notifications · announcements · help · you ─────────────────── -->
  <ul class="navbar-nav flex-row ms-auto header-right">

    <!-- Orion right-panel toggle. Not in the v5 sketch, but it is a working,
         conditional affordance (it renders only where rightPanelUrl is set) and
         nothing asked for its removal. -->
    <li class="nav-item" *ngIf="isRightPanelAvailable()">
      <button type="button"
              class="nav-link"
              title="Toggle Orion panel"
              aria-label="Toggle Orion panel"
              (click)="toggleRightPanel($event)">
        <img src="assets/img/orion.png" alt="Orion" style="height: 20px; width: auto;">
      </button>
    </li>

    <!-- Notifications -->
    <li class="nav-item dropdown dropdown-list" dropdown>
      <button type="button"
              class="nav-link alerts-link dropdown-toggle dropdown-toggle-nocaret"
              dropdownToggle
              title="Notifications"
              aria-label="Notifications">
        <em class="icon-bell"></em>
        <span *ngIf="activeAlerts() > 0" class="badge badge-danger">{{activeAlerts()}}</span>
      </button>
      <div *dropdownMenu class="dropdown-menu dropdown-menu-right">
        <div *ngIf="activeAlerts() <= 0" class="dropdown-item">
          <div class="list-group">
            <div class="list-group-item list-group-item-action">
              <span class="d-flex align-items-center">
                <span class="text-sm">No active notifications</span>
              </span>
            </div>
          </div>
        </div>
        <div *ngIf="activeAlerts() > 0" class="dropdown-item">
          <div class="list-group">
            <div *ngFor="let a of alerts" class="list-group-item list-group-item-action">
              <div class="media">
                <div class="align-self-start me-2">
                  <em [ngClass]="['fa', a.icon || 'fa-bell', 'fa-2x']" [style.color]="a.color"></em>
                </div>
                <div class="media-body">
                  <p class="m-0">{{a.type}}</p>
                  <p class="m-0 text-muted text-sm">{{a.subject}}</p>
                </div>
              </div>
            </div>
            <div class="list-group-item list-group-item-action">
              <span class="d-flex align-items-center">
                <span class="text-sm">{{activeAlerts()}} active notification{{activeAlerts() !== 1 ? 's' : ''}}</span>
                <button type="button"
                        class="btn-inline text-sm text-muted ms-auto"
                        (click)="clearAlerts(); $event.stopPropagation()">Clear all</button>
              </span>
            </div>
          </div>
        </div>
      </div>
    </li>

    <!-- Announcements (Beamer). The ONLY entry point to the news panel as of v6 —
         the duplicate row that used to sit in the Help menu is gone.

         The element has to be id-bearing and always present: beamer-embed.js is
         configured with `selector: '#beamerNewsIcon'` (index.html) and paints its
         unread count into this element. Inside a dropdown it would exist only while
         the dropdown was open, and the unread count — the entire point of the
         affordance — would never show. The count's styling is overridden globally in
         styles.scss, since a third-party-injected node cannot be reached by this
         component's attribute-scoped CSS. Beamer matches on the id, not the tag, so
         the CHAT-911 switch from <a role="button"> to a real <button> is invisible
         to it. -->
    <li class="nav-item">
      <button type="button"
              id="beamerNewsIcon"
              class="nav-link beamer-link"
              title="Announcements"
              aria-label="Announcements"
              (click)="openBeamer()">
        <em class="far fa-newspaper"></em>
      </button>
    </li>

    <!-- Help. v6: a direct link, not a menu.
         The dropdown held three rows and every one of them had a better home:
         the news row duplicated the announcements icon immediately to its left,
         About belongs with the other account-scoped things in the user menu, and
         that left the Knowledge Base alone behind a disclosure. So the '?' just
         goes there.

         *ngIf rather than a dead link: help_uri arrives with the async /settings
         fetch, and a '?' that navigates nowhere is worse than one that appears a
         beat late. -->
    <li class="nav-item" *ngIf="knowledgeBaseUrl">
      <a class="nav-link help-link"
         [href]="knowledgeBaseUrl"
         target="_blank"
         rel="noopener noreferrer"
         title="Knowledge Base"
         aria-label="Knowledge Base">
        <em class="icon-question"></em>
      </a>
    </li>

    <!-- You. Email, not a display name: admin invites are email-only, so there
         is frequently no name to show. Avatar is the first letter of it. -->
    <li class="nav-item dropdown dropdown-list header-menu" dropdown>
      <button type="button"
              class="nav-link user-link dropdown-toggle dropdown-toggle-nocaret"
              dropdownToggle
              [title]="userEmail || 'Account'"
              aria-label="Account menu">
        <span class="user-email">{{userEmail}}</span>
        <span class="user-avatar">{{userInitial}}</span>
      </button>
      <div *dropdownMenu class="dropdown-menu dropdown-menu-right header-menu-panel">
        <div class="header-menu-identity" *ngIf="userEmail">
          <span class="header-menu-identity-email">{{userEmail}}</span>
        </div>

        <a class="header-menu-item" tabindex="0" [routerLink]="'/organization'">
          <em class="icon-user"></em>
          <span>Account</span>
        </a>

        <!-- Appearance. Open to every admin as of CHAT-925 phase 2; the only
             thing that hides it now is a route pinned to light. Exposing the
             control does NOT change what anyone sees by default — ThemeService
             still defaults to light. See showAppearance(). -->
        <div class="header-menu-appearance" *ngIf="showAppearance()" (click)="$event.stopPropagation()">
          <span class="header-menu-section">Appearance</span>
          <div class="appearance-options" role="group" aria-label="Appearance">
            <button type="button"
                    *ngFor="let option of appearanceOptions"
                    class="appearance-option"
                    [class.selected]="themePreference === option.value"
                    [attr.aria-pressed]="themePreference === option.value"
                    [title]="option.label"
                    (click)="setThemePreference(option.value)">
              <em [ngClass]="option.icon"></em>
              <span>{{option.label}}</span>
            </button>
          </div>
        </div>

        <a class="header-menu-item"
           [href]="supportUrl"
           target="_blank"
           rel="noopener noreferrer">
          <em class="icon-support"></em>
          <span>Support</span>
        </a>

        <button type="button" class="header-menu-item" (click)="openAbout()">
          <em class="icon-info"></em>
          <span>About</span>
        </button>

        <div class="header-menu-divider"></div>

        <button type="button" class="header-menu-item" (click)="logout()">
          <em class="icon-logout"></em>
          <span>Sign out</span>
        </button>
      </div>
    </li>
  </ul>

  <app-navsearch [visible]="getNavSearchVisible()" (onclose)="setNavSearchVisible(false)"></app-navsearch>

</nav>

<app-about-dialog></app-about-dialog>
